<template>
  <v-dialog
    :value="value"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    @input="$emit('input', $event)"
  >
    <v-card>
      <v-toolbar
        dark
        color="deep-orange"
      >
        <v-btn
          icon
          dark
          :disabled="loading"
          @click="cerrar"
        >
          <v-icon>
            mdi-close
          </v-icon>
        </v-btn>

        <v-toolbar-title>
          Vista previa
          <span
            v-if="configuracion"
            class="font-weight-light"
          >
            — {{ configuracion.nombre }}
          </span>
        </v-toolbar-title>

        <v-spacer></v-spacer>

        <v-btn
          text
          dark
          :loading="loading"
          :disabled="
            !configuracion ||
            !configuracion.codigo
          "
          @click="consultar"
        >
          Actualizar
          <v-icon right>
            mdi-refresh
          </v-icon>
        </v-btn>
      </v-toolbar>

      <v-container
        fluid
        class="vista-previa-container"
      >
        <v-row>
          <v-col
            cols="12"
            md="4"
          >
            <v-card
              outlined
              class="fill-height"
            >
              <v-card-text>
                <div
                  class="
                    caption
                    text-uppercase
                    grey--text
                    text--darken-1
                  "
                >
                  Periodo de registro
                </div>

                <div
                  v-if="periodo"
                  class="
                    title
                    font-weight-medium
                    mt-2
                  "
                >
                  {{
                    formatearFecha(
                      periodo.fecha_inicio
                    )
                  }}
                  <v-icon
                    small
                    class="mx-2"
                  >
                    mdi-arrow-right
                  </v-icon>
                  {{
                    formatearFecha(
                      periodo.fecha_fin
                    )
                  }}
                </div>

                <div
                  v-else
                  class="grey--text mt-2"
                >
                  Sin periodo disponible
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            md="4"
          >
            <v-card
              outlined
              class="fill-height"
            >
              <v-card-text>
                <div
                  class="
                    caption
                    text-uppercase
                    grey--text
                    text--darken-1
                  "
                >
                  Registros encontrados
                </div>

                <div
                  class="
                    display-1
                    font-weight-medium
                    mt-2
                  "
                >
                  {{
                    formatearCantidad(total)
                  }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            md="4"
          >
            <v-card
              outlined
              class="fill-height"
            >
              <v-card-text>
                <div
                  class="
                    caption
                    text-uppercase
                    grey--text
                    text--darken-1
                  "
                >
                  Registros mostrados
                </div>

                <div
                  class="
                    display-1
                    font-weight-medium
                    mt-2
                  "
                >
                  {{
                    formatearCantidad(
                      mostrados
                    )
                  }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-alert
          v-if="error"
          type="error"
          outlined
          prominent
          class="mt-4"
        >
          {{ mensajeError }}
        </v-alert>

        <v-alert
          v-else-if="
            !loading &&
            consultado &&
            total === 0
          "
          type="info"
          outlined
          prominent
          class="mt-4"
        >
          No se encontraron registros para
          el periodo solicitado.
        </v-alert>

        <v-card
          v-else
          outlined
          class="mt-4"
        >
          <v-card-title>
            Resultados

            <v-spacer></v-spacer>

            <v-chip
              v-if="total > mostrados"
              small
              outlined
              color="primary"
            >
              Mostrando los primeros
              {{ mostrados }}
            </v-chip>
          </v-card-title>

          <v-divider></v-divider>

          <div
            class="
              vista-previa-tabla
              overflow-x-auto
            "
          >
            <v-data-table
              :headers="columnasTabla"
              :items="filas"
              :loading="loading"
              :items-per-page="-1"
              hide-default-footer
              disable-pagination
              fixed-header
              height="calc(100vh - 390px)"
              class="
                elevation-0
                vista-previa-data-table
              "
            >
              <template v-slot:no-data>
                No hay información para mostrar
              </template>

            </v-data-table>
          </div>
        </v-card>

        <v-alert
          v-if="
            total > mostrados &&
            mostrados > 0
          "
          dense
          outlined
          type="info"
          class="mt-4 mb-0"
        >
          La vista previa muestra únicamente
          los primeros {{ mostrados }} registros
          de {{ formatearCantidad(total) }}.
          El archivo completo incluirá todos los
          registros encontrados.
        </v-alert>
      </v-container>

      <v-overlay
        :value="loading"
        absolute
        opacity="0.25"
      >
        <div class="text-center">
          <v-progress-circular
            indeterminate
            size="64"
            width="5"
          ></v-progress-circular>

          <div class="title mt-4">
            Consultando vista previa...
          </div>
        </div>
      </v-overlay>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import {
  obtenerVistaPreviaExportacionProgramada,
} from "@/utils/exportacionesProgramadasApi";

export default {
  name:
    "VistaPreviaExportacionDialog",

  props: {
    value: {
      type: Boolean,
      default: false,
    },

    configuracion: {
      type: Object,
      default: null,
    },
  },

  data: () => ({
    loading: false,
    consultado: false,
    error: false,
    mensajeError: "",
    periodo: null,
    total: 0,
    mostrados: 0,
    columnas: [],
    filas: [],
  }),

  computed: {
    columnasTabla() {
      return this.columnas.map(
        columna => ({
          text:
            columna.text ||
            columna.value,
          value:
            columna.value,
          align:
            columna.align ||
            "start",
          sortable: false,
          width: "220px",
          class:
            "vista-previa-header",
        })
      );
    },
  },

  watch: {
    value(valor) {
      if (valor) {
        this.preparar();
      }
    },

    configuracion: {
      deep: true,
      handler() {
        if (
          this.value &&
          this.configuracion &&
          this.configuracion.codigo
        ) {
          this.preparar();
        }
      },
    },
  },

  methods: {
    async preparar() {
      this.limpiar();

      await this.$nextTick();

      if (
        this.configuracion &&
        this.configuracion.codigo
      ) {
        await this.consultar();
      }
    },

    limpiar() {
      this.error = false;
      this.mensajeError = "";
      this.consultado = false;
      this.periodo = null;
      this.total = 0;
      this.mostrados = 0;
      this.columnas = [];
      this.filas = [];
    },

    async consultar() {
      if (
        !this.configuracion ||
        !this.configuracion.codigo
      ) {
        return;
      }

      this.loading = true;
      this.error = false;
      this.mensajeError = "";

      try {
        const resultado =
          await obtenerVistaPreviaExportacionProgramada(
            this.$session,
            this.configuracion.codigo
          );

        if (!resultado.correcto) {
          this.mostrarError(
            resultado.mensaje ||
              "No fue posible obtener la vista previa"
          );
          return;
        }

        this.periodo =
          resultado.periodo_registro ||
          null;

        this.total =
          Number(resultado.total) || 0;

        this.mostrados =
          Number(resultado.mostrados) || 0;

        this.columnas =
          Array.isArray(
            resultado.columnas
          )
            ? resultado.columnas
            : [];

        this.filas =
          Array.isArray(resultado.filas)
            ? resultado.filas
            : [];

        this.consultado = true;
      } catch (error) {
        console.error(error);

        if (
          error.response &&
          error.response.status === 403
        ) {
          this.$router.push("/403");
          return;
        }

        this.mostrarError(
          error.response &&
          error.response.data &&
          error.response.data.mensaje
            ? error.response.data.mensaje
            : "No fue posible obtener la vista previa"
        );
      } finally {
        this.loading = false;
      }
    },

    mostrarError(mensaje) {
      this.error = true;
      this.mensajeError = mensaje;
      this.consultado = true;
    },

    cerrar() {
      if (this.loading) {
        return;
      }

      this.$emit("input", false);
    },

    formatearFecha(fecha) {
      if (!fecha) {
        return "—";
      }

      const valor = moment(
        fecha,
        "YYYY-MM-DD",
        true
      );

      if (!valor.isValid()) {
        return fecha;
      }

      return valor.format(
        "DD/MM/YYYY"
      );
    },

    formatearCantidad(valor) {
      const numero = Number(valor);

      if (!Number.isFinite(numero)) {
        return "0";
      }

      return numero.toLocaleString(
        "es-MX"
      );
    },

    valorCelda(item, columna) {
      if (
        !item ||
        typeof item !== "object"
      ) {
        return "";
      }

      const valor = item[columna];

      if (
        valor === null ||
        valor === undefined
      ) {
        return "";
      }

      if (typeof valor === "object") {
        try {
          return JSON.stringify(valor);
        } catch (error) {
          return String(valor);
        }
      }

      return String(valor);
    },
  },
};
</script>

<style scoped>
.vista-previa-container {
  background: #f5f6f8;
  min-height:
    calc(100vh - 64px);
  padding: 24px;
}

.vista-previa-tabla {
  width: 100%;
}

.vista-previa-data-table {
  min-width: max-content;
}

.vista-previa-celda {
  width: 210px;
  max-width: 210px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

::v-deep .vista-previa-header {
  min-width: 220px;
  white-space: nowrap;
}

::v-deep
  .vista-previa-data-table
  table {
  width: max-content;
  min-width: 100%;
}

@media (max-width: 600px) {
  .vista-previa-container {
    padding: 8px;
  }
}
</style>
